/**
 * Collection Filters System - Moon Moon Agency v2.1
 * Shopify collection filtering with AJAX updates, custom events, and UI sync
 */

// Initialisation de l'objet queryParams pour stocker les paramètres de requête
Shopify.queryParams = {};

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const COLLECTION_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,

	// Scroll infini : le lot suivant est demande quand la sentinelle arrive a 1,5 hauteur
	// d'ecran du viewport, pour que les produits soient la avant que l'utilisateur voie la fin.
	infiniteRootMargin: '0px 0px 150% 0px',

	// Section legere demandee en AJAX pour le filtrage (nom de fichier en minuscules,
	// comme l'attend la Section Rendering API). Voir sections/MM-Collection-Grid-Fragment.liquid.
	fragmentSectionId: 'mm-collection-grid-fragment',
	
	// UI Settings
	defaultOpenFilters: 0, // Panel : tous les accordeons fermes a l'ouverture
	
	// Price Slider Settings
	priceGap: 10,
	
	// Selectors - Using data attributes for better separation of concerns
	selectors: {
		form: '[data-collection-form]',
		grid: '[data-collection-grid]',
		gridCollection: '[data-grid-collection]',
		shopifySection: '.shopify-section', // Keep as class (Shopify generated)
		activeFilters: '[data-active-filters]',
		reloadElements: '[data-collection-reload]',
		sortCurrent: '[data-sort-current]',

		// Filter panel (drawer droite)
		filterPanel: '[data-filter-panel]',
		filterOpen: '[data-filter-open]',
		filterClose: '[data-filter-close]',
		filterSubmit: '[data-filter-submit]',
		htmlElement: 'html',

		// Switch colonnes de la grille
		gridColumns: '[data-grid-columns]',
		dayNightToggle: '[data-day-night-toggle]',

		// Scroll infini
		infiniteNext: '[data-infinite-next]',
		infiniteZone: '[data-reload-id="count-bottom"]',
		gridFiller: '.mm-grid-filler',
		
		// Filter accordions
		filterHeaders: '[data-filter-header]',
		
		// Price sliders
		minRange: '[data-price-min]',
		maxRange: '[data-price-max]',
		sliderTrack: '[data-price-track]',
		minRangeValue: '[data-price-min-value]',
		maxRangeValue: '[data-price-max-value]',
		priceMinValue: '[data-price-min-display]',
		priceMaxValue: '[data-price-max-display]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Collection Event Emitter

// DOM Selectors Cache
const CollectionDOM = {
	get form() { return document.querySelector(COLLECTION_CONFIG.selectors.form); },
	get grid() { return document.querySelector(COLLECTION_CONFIG.selectors.grid); },
	get gridCollection() { return document.querySelector(COLLECTION_CONFIG.selectors.gridCollection); },
	get sortCurrent() { return document.querySelector(COLLECTION_CONFIG.selectors.sortCurrent); },
	get html() { return document.querySelector(COLLECTION_CONFIG.selectors.htmlElement); },
	get filterPanel() { return document.querySelector(COLLECTION_CONFIG.selectors.filterPanel); },
	get filterOpenBtns() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.filterOpen); },
	get gridColumnsBtns() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.gridColumns); },
	
	get activeFilters() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters); },
	get reloadElements() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements); },
	get filterHeaders() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders); },
	get formInputs() { return this.form?.querySelectorAll('input') || []; },
	
	// Price sliders
	get priceSliders() {
		return {
			minRange: document.querySelector(COLLECTION_CONFIG.selectors.minRange),
			maxRange: document.querySelector(COLLECTION_CONFIG.selectors.maxRange),
			sliderTrack: document.querySelector(COLLECTION_CONFIG.selectors.sliderTrack),
			minRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.minRangeValue),
			maxRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.maxRangeValue)
		};
	},
	
	get shopifySection() {
		const gridElem = this.gridCollection;
		return gridElem?.closest(COLLECTION_CONFIG.selectors.shopifySection);
	}
};

/*********************************************************************************************
 * 🔌 CORE COLLECTION API
 */
const CollectionAPI = {
	// AbortController de la derniere requete en vol : garantit le "latest-wins".
	// Une reponse obsolete (reseau lent, clics successifs) ne peut plus ecraser une plus recente.
	_controller: null,

	async request(url) {
		window.emit('collection:api-start', { url });

		// Annule la requete precedente encore en vol avant d'en lancer une nouvelle.
		this._controller?.abort();
		const controller = new AbortController();
		this._controller = controller;

		// Timeout dur (apiTimeout) : evite une grille figee en mm-grid-loading sur reseau suspendu.
		const timeoutId = setTimeout(() => controller.abort(), COLLECTION_CONFIG.apiTimeout);

		try {
			const response = await fetch(url, { signal: controller.signal });
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is JSON (Section Rendering API) or HTML (fallback)
			const contentType = response.headers.get('content-type');
			const data = contentType && contentType.includes('application/json')
				? await response.json()
				: await response.text();

			window.emit('collection:api-success', { url, responseType: typeof data });
			return data;
		} catch (error) {
			window.emit('collection:api-error', { url, error: error.message });
			throw error;
		} finally {
			clearTimeout(timeoutId);
			if (this._controller === controller) this._controller = null;
		}
	},

	getSectionId() {
		const section = CollectionDOM.shopifySection;
		if (!section) {
			throw new Error('Collection grid element or shopify section not found');
		}
		return section.id;
	},

	getSectionIdShort() {
		return this.getSectionId().replace('shopify-section-', '');
	},

	async refreshWithFilters(url_filters) {
		window.emit('collection:filter-start', { filters: url_filters });
		
		try {
			const currentUrl = `${window.location.pathname}`;

			// On demande la section fragment legere (grille + count + pagination, SANS les filtres
			// ni le hero) plutot que la section principale entiere : ~15% de transfert en moins et
			// surtout pas de re-rendu serveur du formulaire de filtres a chaque clic.
			const fragmentId = COLLECTION_CONFIG.fragmentSectionId;
			const requestUrl = `${currentUrl}?${url_filters}&sections=${fragmentId}`;
			let response = await this.request(requestUrl);

			if (!CollectionDOM.grid) {
				throw new Error('Collection grid element not found');
			}

			// Handle different response types
			let newSectionHtml;
			if (typeof response === 'object') {
				newSectionHtml = response[fragmentId];

				// Fallback : si le fragment n'est pas deploye (null), reessayer avec la section principale.
				if (newSectionHtml == null) {
					const mainId = this.getSectionIdShort();
					const fallback = await this.request(`${currentUrl}?${url_filters}&sections=${mainId}`);
					newSectionHtml = (typeof fallback === 'object') ? fallback[mainId] : fallback;
				}
			} else if (typeof response === 'string') {
				// Fallback: HTML response
				const newPageEl = document.createElement('DIV');
				newPageEl.innerHTML = response;
				const newSection = newPageEl.querySelector(COLLECTION_CONFIG.selectors.grid);
				if (!newSection) {
					throw new Error('Required collection elements not found in response');
				}
				newSectionHtml = newSection.outerHTML;
			} else {
				throw new Error('Invalid response format');
			}

			this.updateDOM(newSectionHtml, currentUrl, url_filters);
			window.emit('collection:filter-success', { filters: url_filters, url: `${currentUrl}?${url_filters}` });
			
		} catch (error) {
			// Requete annulee par une plus recente (ou par le timeout) : ne PAS degrader vers un
			// reload — une autre requete est en cours ou l'utilisateur a deja reagi.
			if (error.name === 'AbortError') {
				window.emit('collection:filter-aborted', { filters: url_filters });
				return;
			}

			window.emit('collection:filter-error', { error: error.message, filters: url_filters });

			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-grid-loading');
			}

			// Fallback: reload page if AJAX fails
			window.location.search = url_filters;
		}
	},

	updateDOM(newSectionHtml, currentUrl, url_filters) {
		// Create temporary element to parse new HTML
		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = newSectionHtml;

		// Update collection grid content (products only)
		const newGridCollection = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		if (newGridCollection && CollectionDOM.grid) {
			CollectionDOM.grid.innerHTML = newGridCollection.innerHTML;
		}

		// Update active filters
		const newActiveFilters = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters);
		CollectionDOM.activeFilters.forEach((currentFilter, index) => {
			if (newActiveFilters[index]) {
				currentFilter.innerHTML = newActiveFilters[index].innerHTML;
			}
		});

		// Update elements with 'data-collection-reload'. Matching par data-reload-id (stable) avec
		// fallback sur l'index pour les elements sans id. Le fragment peut contenir MOINS d'elements
		// que la page (ex: le CTA des filtres n'est pas dans la grille) : la cle evite tout decalage.
		const newReloadElems = [...tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements)];
		const newById = {};
		newReloadElems.forEach((el, i) => {
			const id = el.dataset.reloadId;
			if (id) newById[id] = el;
		});

		CollectionDOM.reloadElements.forEach((currentElem, index) => {
			const id = currentElem.dataset.reloadId;
			const source = (id && newById[id]) ? newById[id] : (id ? null : newReloadElems[index]);
			if (!source) return; // pas de correspondance fiable → on ne touche pas

			// CTA du panel : on ne remplace que le texte du <span> (le bouton garde ses listeners).
			// Les autres zones (compteurs, pagination) sont re-rendues en entier.
			const currentSpan = id === 'cta-submit' ? currentElem.querySelector('span') : null;
			const newSpan = currentSpan ? source.querySelector('span') : null;

			if (currentSpan && newSpan) {
				currentSpan.textContent = newSpan.textContent;
			} else {
				currentElem.innerHTML = source.innerHTML;
			}
		});

		// Update URL in history without reloading page
		// Sans aucun filtre (reinitialisation), pas de « ? » orphelin
		const newUrl = url_filters ? `${currentUrl}?${url_filters}` : currentUrl;
		window.history.replaceState(null, document.title, newUrl);
		
		if (CollectionDOM.grid) {
			CollectionDOM.grid.classList.remove('mm-grid-loading');
		}
		
		// Pas de re-init des filtres : updateDOM ne re-rend que la grille, le formulaire
		// de filtres reste en place (listeners deja bindes une fois via CollectionFilters.init).

		ActiveFiltersClear.update();

		// Nouvelle sentinelle (zone count-bottom re-rendue) : le scroll infini repart de la page 2
		InfiniteScroll.cleanNextLink();
		InfiniteScroll.observe();

		// Re-initialize add to cart forms after DOM update (les cards ont ete re-rendues)
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}
	}
};

/*********************************************************************************************
 * ♾️ INFINITE SCROLL
 * Remplace la pagination : la sentinelle [data-infinite-next] (lien reel vers la page suivante,
 * rendu par mm-co-grid-zone) est observee ; a son approche, on demande la page suivante via le
 * fragment et on ajoute ses cards a la grille. Absence de sentinelle = derniere page.
 */
const InfiniteScroll = {
	_observer: null,
	_controller: null,

	init() {
		if (!('IntersectionObserver' in window)) return; // fallback : lien page suivante natif

		this._observer = new IntersectionObserver((entries) => {
			if (entries.some(entry => entry.isIntersecting)) this.loadNext();
		}, { rootMargin: COLLECTION_CONFIG.infiniteRootMargin });

		// Un filtrage remplace la grille : un lot en vol deviendrait obsolete, on l'annule
		document.addEventListener('collection:filter-start', () => this._controller?.abort());

		this.observe();
	},

	// (Re)observe la zone de la sentinelle courante. Une zone deja visible declenche aussitot le
	// callback : les lots s'enchainent tant que la grille ne depasse pas la zone de pre-chargement.
	// On observe la zone (compteur) et non le lien : en .visually-hidden (clip 0 + marge -1px),
	// le lien n'est jamais vu comme intersectant par IntersectionObserver.
	observe() {
		if (!this._observer) return;
		this._observer.disconnect();
		const { infiniteNext, infiniteZone } = COLLECTION_CONFIG.selectors;
		const zone = document.querySelector(infiniteZone);
		if (zone && document.querySelector(infiniteNext)) this._observer.observe(zone);
	},

	async loadNext() {
		const { selectors, fragmentSectionId } = COLLECTION_CONFIG;
		const link = document.querySelector(selectors.infiniteNext);
		const grid = CollectionDOM.grid;
		if (this._controller || !link || !grid) return; // un lot deja en vol

		const controller = new AbortController();
		this._controller = controller;
		this._observer.disconnect();

		const url = new URL(link.href);
		url.searchParams.set('sections', fragmentSectionId);
		window.emit('collection:infinite-start', { url: link.href });

		try {
			const response = await fetch(url, { signal: controller.signal });
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

			const html = (await response.json())[fragmentSectionId];
			if (html == null) throw new Error('Fragment section not found');

			const tempEl = document.createElement('DIV');
			tempEl.innerHTML = html;
			const newGrid = tempEl.querySelector(selectors.grid);
			if (!newGrid) throw new Error('Collection grid not found in response');

			// Les fillers ne completent que la derniere ligne : ceux du lot precedent sont retires,
			// ceux du nouveau lot recalcules sur le total (son residu ne tient pas compte du reste).
			grid.querySelectorAll(selectors.gridFiller).forEach(el => el.remove());
			grid.append(...[...newGrid.children].filter(el => !el.matches(selectors.gridFiller)));
			this.fillLastRow(grid);

			// Compteur "X produits sur Y" + nouvelle sentinelle (absente sur la derniere page)
			const zone = document.querySelector(selectors.infiniteZone);
			const newZone = tempEl.querySelector(selectors.infiniteZone);
			if (zone && newZone) zone.innerHTML = newZone.innerHTML;
			this.cleanNextLink();

			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}

			window.emit('collection:infinite-success', { url: link.href });
			this.observe();
		} catch (error) {
			// Annule par un filtrage : updateDOM re-observera la nouvelle sentinelle
			if (error.name === 'AbortError') return;
			// Echec reseau : on n'insiste pas (pas de boucle de requetes), le lien reste suivable
			window.emit('collection:infinite-error', { url: link.href, error: error.message });
		} finally {
			if (this._controller === controller) this._controller = null;
		}
	},

	// Grille "toujours pleine" : complete jusqu'au prochain multiple de 12 (PPCM des colonnes)
	fillLastRow(grid) {
		const residu = grid.children.length % 12;
		if (!residu) return;
		for (let i = residu; i < 12; i++) {
			const filler = document.createElement('div');
			filler.className = 'mm-grid-filler';
			filler.setAttribute('aria-hidden', 'true');
			grid.append(filler);
		}
	},

	// Le lien rendu dans une reponse Section Rendering peut conserver ?sections= : on le retire
	// pour qu'il reste une vraie URL de page (crawl, ouverture dans un nouvel onglet).
	cleanNextLink() {
		const link = document.querySelector(COLLECTION_CONFIG.selectors.infiniteNext);
		if (!link) return;
		const url = new URL(link.href);
		if (!url.searchParams.has('sections')) return;
		url.searchParams.delete('sections');
		link.href = url.toString();
	}
};

/*********************************************************************************************
 * 🧹 ACTIVE FILTERS CLEAR (lien "Reinitialiser" masque sans filtre actif)
 * Meme regle que mm-filter.liquid : une case cochee, ou un curseur prix hors de ses bornes.
 */
const ActiveFiltersClear = {
	update() {
		const form = CollectionDOM.form;
		if (!form) return;

		const { minRange, maxRange } = CollectionDOM.priceSliders;
		const hasActive = !!form.querySelector('input[type="checkbox"]:checked')
			|| (minRange && Number(minRange.value) > Number(minRange.min))
			|| (maxRange && Number(maxRange.value) < Number(maxRange.max));

		CollectionDOM.activeFilters.forEach(link => { link.hidden = !hasActive; });
	},

	/**
	 * Reinitialise en AJAX puis ferme le panel, avec la meme animation que
	 * « Voir les resultats ». Le lien rechargeait la page : le panel disparaissait
	 * d'un coup, sans transition. Le href reste en repli sans JS.
	 *
	 * Le tri choisi est conserve (comme le href, qui garde ?sort_by=).
	 */
	reset(event) {
		const form = CollectionDOM.form;
		if (!form) return; // pas de formulaire : le lien recharge la page
		event.preventDefault();

		form.querySelectorAll('input[type="checkbox"]:checked').forEach(input => { input.checked = false; });

		const { minRange, maxRange } = CollectionDOM.priceSliders;
		if (minRange) minRange.value = minRange.min;
		if (maxRange) maxRange.value = maxRange.max;
		PriceSliders.updateRange();

		this.update();
		CollectionFilters.submitForm();
		FilterPanel.close();
		window.emit('collection:filters-reset');
	}
};

/*********************************************************************************************
 * 🔍 COLLECTION FILTERS MANAGEMENT
 */
const CollectionFilters = {
	_bound: false,

	init() {
		if (!CollectionDOM.form) {
			window.emit('collection:form-error', { error: 'Collection facets form not found' });
			return;
		}

		// Le formulaire de filtres n'est PAS re-rendu par updateDOM (seule la grille l'est).
		// On binde donc une seule fois — re-binder a chaque filtrage est inutile (les inputs
		// ne sont jamais detruits) et risque d'empiler des listeners.
		if (this._bound) return;

		const inputs = CollectionDOM.formInputs;
		inputs.forEach(input => {
			input.addEventListener('change', this.handleInputChange);
		});

		this._bound = true;
		window.emit('collection:filters-initialized', { inputCount: inputs.length });
	},

	handleInputChange(event) {
		window.emit('collection:input-change', {
			name: event.target.name || event.target.id,
			value: event.target.value,
			checked: event.target.checked
		});

		// Radio de tri : reporte le libelle choisi dans le header de l'accordeon
		if (event.target.name === 'sort_by' && CollectionDOM.sortCurrent) {
			CollectionDOM.sortCurrent.textContent = event.target.labels[0]?.textContent.trim() ?? '';
		}

		// Lien "Reinitialiser" : affiche/masque des la selection, sans attendre la reponse AJAX
		ActiveFiltersClear.update();

		// Debounce : evite une requete plein-poids par case cochee en rafale
		CollectionFilters.submitFormDebounced();
	},

	async submitForm() {
		if (!CollectionDOM.form || !CollectionDOM.grid) {
			window.emit('collection:form-error', { error: 'Required elements not found' });
			return;
		}
		
		try {
			window.emit('collection:form-submit-start');
			
			const params = new URLSearchParams(new FormData(CollectionDOM.form));

			// Les curseurs de prix sont toujours soumis : une borne restee a son extreme ne
			// filtre rien, on la retire de l'URL (meme regle que ActiveFiltersClear.update).
			const { minRange, maxRange } = CollectionDOM.priceSliders;
			if (minRange && Number(minRange.value) <= Number(minRange.min)) params.delete(minRange.name);
			if (maxRange && Number(maxRange.value) >= Number(maxRange.max)) params.delete(maxRange.name);

			const url_filters = params.toString();
			
			CollectionDOM.grid.classList.add('mm-grid-loading');
			
			await CollectionAPI.refreshWithFilters(url_filters);
			
			window.emit('collection:form-submit-success', { filters: url_filters });
			
		} catch (error) {
			window.emit('collection:form-submit-error', { error: error.message });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-grid-loading');
			}
		}
	},

	// Version debouncee de submitForm — partage un seul timer (cree paresseusement).
	// Utilisee pour les declencheurs en rafale (cases a cocher, slider prix).
	submitFormDebounced(...args) {
		if (!this._debounced) {
			this._debounced = window.debounce(() => this.submitForm(), COLLECTION_CONFIG.debounceDelay);
		}
		this._debounced(...args);
	}
};

/*********************************************************************************************
 * 🗂️ FILTER PANEL (drawer droite, toutes tailles d'ecran)
 */
const FilterPanel = {
	_trigger: null,

	isOpen() {
		return CollectionDOM.filterPanel?.classList.contains('mm-open') ?? false;
	},

	setExpanded(expanded) {
		CollectionDOM.filterOpenBtns.forEach(btn => btn.setAttribute('aria-expanded', String(expanded)));
	},

	open(trigger = null) {
		const panel = CollectionDOM.filterPanel;
		const html = CollectionDOM.html;
		if (!panel || !html || this.isOpen()) return;

		this._trigger = trigger;
		panel.classList.add('mm-open');
		html.classList.add('stop-scrolling');
		this.setExpanded(true);
		panel.querySelector(COLLECTION_CONFIG.selectors.filterClose)?.focus({ preventScroll: true });
		window.emit('collection:filter-panel-opened');
	},

	close() {
		const panel = CollectionDOM.filterPanel;
		const html = CollectionDOM.html;
		if (!panel || !html || !this.isOpen()) return;

		panel.classList.remove('mm-open');
		html.classList.remove('stop-scrolling');
		this.setExpanded(false);
		this._trigger?.focus({ preventScroll: true });
		this._trigger = null;
		window.emit('collection:filter-panel-closed');
	},

	// Dialog modal : Tab / Shift+Tab bouclent dans le panel
	trapFocus(e) {
		const focusables = [...CollectionDOM.filterPanel.querySelectorAll('button, a[href], input, select')]
			.filter(el => !el.disabled && el.offsetParent !== null);
		if (!focusables.length) return;

		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	},

	checkDrawer() {
		if (document.location.href.includes('open_filters')) {
			this.open();
		}
	}
};

/*********************************************************************************************
 * 🔲 GRID COLUMNS (switch 2 / 4 / 6 colonnes desktop)
 */
const GridColumns = {
	init() {
		const buttons = CollectionDOM.gridColumnsBtns;

		buttons.forEach(btn => {
			btn.addEventListener('click', () => {
				const columns = btn.dataset.gridColumns;
				CollectionDOM.gridCollection?.setAttribute('data-columns', columns);
				buttons.forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
				window.emit('collection:grid-columns-change', { columns });
			});
		});
	}
};

/*********************************************************************************************
 * 🌙 DAY / NIGHT (switch : affiche la 2e photo des produits en mode Nuit)
 */
const DayNight = {
	init() {
		const toggle = document.querySelector(COLLECTION_CONFIG.selectors.dayNightToggle);
		if (!toggle) return;

		// Des l'approche du switch (survol, focus, toucher) : les 2es photos des cartes a slider
		// sortent du display none et se chargent, le fondu ne joue pas sur une image vide
		const prepare = () => CollectionDOM.gridCollection?.setAttribute('data-day-night-ready', '');
		['pointerenter', 'focus', 'touchstart'].forEach(type => {
			toggle.addEventListener(type, prepare, { once: true, passive: true });
		});

		toggle.addEventListener('click', () => {
			prepare();
			const night = toggle.getAttribute('aria-checked') !== 'true';
			toggle.setAttribute('aria-checked', String(night));
			CollectionDOM.gridCollection?.setAttribute('data-day-night', night ? 'night' : 'day');
			window.emit('collection:day-night-change', { night });
		});
	}
};

/*********************************************************************************************
 * 📱 GRID NAV MOBILE (bandeau horizontal : l'element actif est ramene dans la vue)
 */
const GridNavMobile = {
	init() {
		const band = document.querySelector('[data-grid-nav-mobile]');
		const active = band?.querySelector('[aria-current="page"]');
		if (!active) return;

		// scrollLeft plutot que scrollIntoView : ne fait pas defiler la page verticalement
		band.scrollLeft = active.offsetLeft - (band.clientWidth - active.offsetWidth) / 2;
	}
};

/*********************************************************************************************
 * 🎛️ FILTER ACCORDIONS
 */
const FilterAccordions = {
	init() {
		const filtersHeaders = CollectionDOM.filterHeaders;

		filtersHeaders.forEach((filtersHeader, index) => {
			// Activate first three filters by default
			if (index < COLLECTION_CONFIG.defaultOpenFilters) {
				filtersHeader.classList.add('mm-active');
				filtersHeader.setAttribute('aria-expanded', 'true');
			}

			// Add click event to open/close filters
			filtersHeader.addEventListener('click', function() {
				const active = filtersHeader.classList.toggle('mm-active');
				filtersHeader.setAttribute('aria-expanded', String(active));
				window.emit('collection:filter-accordion-toggle', { index, active });
			});
		});
		
		window.emit('collection:accordions-initialized', { count: filtersHeaders.length });
	}
};

/*********************************************************************************************
 * 💰 PRICE SLIDERS
 */
const PriceSliders = {
	get elements() {
		return CollectionDOM.priceSliders;
	},
	
	isReady() {
		const elements = this.elements;
		return elements.minRange && elements.maxRange && 
				elements.sliderTrack && elements.minRangeValue && 
				elements.maxRangeValue;
	},

	init() {
		if (!this.isReady()) return;
		
		const { minRange, maxRange } = this.elements;
		
		minRange.addEventListener('input', () => {
			if (parseInt(minRange.value) > parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap) {
				minRange.value = maxRange.value - COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		maxRange.addEventListener('input', () => {
			if (parseInt(maxRange.value) < parseInt(minRange.value) + COLLECTION_CONFIG.priceGap) {
				maxRange.value = parseInt(minRange.value) + COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});
		
		// Initial update
		this.updateRange();
		window.emit('collection:price-sliders-initialized');
	},

	updateRange() {
		if (!this.isReady()) {
			window.emit('collection:price-error', { error: 'Price slider elements not found' });
			return;
		}
		
		const { minRange, maxRange, sliderTrack, minRangeValue, maxRangeValue } = this.elements;
		
		const minValue = parseInt(minRange.value, 10); 
		const maxValue = parseInt(maxRange.value, 10);

		// Update min and max values display
		const minValueEl = minRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMinValue);
		const maxValueEl = maxRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMaxValue);
		
		if (minValueEl) minValueEl.innerHTML = minValue;
		if (maxValueEl) maxValueEl.innerHTML = maxValue;
		
		// Calculate percentages for track background
		const minPercent = (minValue / minRange.max) * 100;
		const maxPercent = (maxValue / maxRange.max) * 100;

		// Update track background to reflect selected price range
		// Plage selectionnee en blanc plein, hors plage en blanc 30%
		const outside = 'rgb(255 255 255 / 0.3)';
		sliderTrack.style.background = `linear-gradient(to right, ${outside} ${minPercent}%, var(--white) ${minPercent}%, var(--white) ${maxPercent}%, ${outside} ${maxPercent}%)`;
		
		window.emit('collection:price-update', { minValue, maxValue, minPercent, maxPercent });
	}
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS (for backward compatibility)
 */
window.mmOpenFilters = () => FilterPanel.open();
window.mmCloseFilters = () => FilterPanel.close();

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	window.emit('collection:init-start');
	
	try {
		CollectionFilters.init();
		FilterAccordions.init();
		PriceSliders.init();
		GridColumns.init();
		DayNight.init();
		GridNavMobile.init();
		InfiniteScroll.init();
		ActiveFiltersClear.update();
		FilterPanel.checkDrawer();
		
		window.emit('collection:init-success');
	} catch (error) {
		window.emit('collection:init-error', { error: error.message });
		window.log('error', 'Collection', 'Collection initialization failed', { error: error.message });
	}
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	const { filterOpen, filterClose, filterSubmit } = COLLECTION_CONFIG.selectors;

	// Ouverture du panel (bouton Trier / Filtrer de la toolbar)
	document.querySelectorAll(filterOpen).forEach((btn) => {
		btn.addEventListener('click', () => {
			if (typeof window.mmCloseMobileMenu === 'function') {
				window.mmCloseMobileMenu();
			}
			FilterPanel.open(btn);
		});
	});

	// Fermeture : bouton close + overlay
	document.querySelectorAll(filterClose).forEach((el) => {
		el.addEventListener('click', () => FilterPanel.close());
	});

	// CTA "Voir les X resultats" : les filtres s'appliquent deja en AJAX, on ferme le panel
	document.querySelector(filterSubmit)?.addEventListener('click', () => FilterPanel.close());

	// "Reinitialiser" : reset AJAX + fermeture animee (le lien n'est jamais re-rendu,
	// updateDOM ne remplace que son contenu)
	CollectionDOM.activeFilters.forEach((link) => {
		link.addEventListener('click', (e) => ActiveFiltersClear.reset(e));
	});

	document.addEventListener('keydown', (e) => {
		if (!FilterPanel.isOpen()) return;
		if (e.key === 'Escape') FilterPanel.close();
		if (e.key === 'Tab') FilterPanel.trapFocus(e);
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	['init-success', 'filter-success', 'input-change', 'form-submit-start', 'price-update', 'sort-change'].forEach(event => {
		document.addEventListener(`collection:${event}`, e =>
			console.log(`🏷️ [COLLECTION] ${event}:`, e.detail)
		);
	});
}